/**
 * ModerationCaseCardSection Component
 *
 * Reusable section wrapper for different content types in moderation cards.
 * Delegates to ContentCard.Section from the design system for layout,
 * icon, title, and count badge rendering.
 */

import { ContentCard } from '@soteria/design-system-ve';
import type { ReactNode, ComponentType } from 'react';

export interface ModerationCaseCardSectionProps {
  /**
   * Icon component to display in section header
   */
  icon: ComponentType;

  /**
   * Section title text
   */
  title: string;

  /**
   * Section content
   */
  children: ReactNode;

  /**
   * Whether to show this section (default: true)
   */
  show?: boolean;

  /**
   * Optional count badge to display after title (e.g., 3 for "Text (3)")
   * Only shows when count > 1
   */
  count?: number;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * When true, hides the section header (icon, title, count).
   * Content sections use this since types are shown in the card header badges.
   */
  hideHeader?: boolean;
}

/**
 * Section component for ModerationCaseCard
 *
 * @example
 * ```tsx
 * <ModerationCaseCardSection icon={Chat} title="Text" count={3}>
 *   <p>Multiple text messages...</p>
 * </ModerationCaseCardSection>
 * ```
 */
export function ModerationCaseCardSection(props: ModerationCaseCardSectionProps) {
  return <ContentCard.Section {...props} />;
}
